"use client"

import React, {useState, useEffect, useRef} from "react"
import {Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis, Legend, Tooltip, ResponsiveContainer} from "recharts"
import {CircleHelp, Download, ChevronDown } from "lucide-react"
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"
import {Switch} from "@/components/ui/switch"
import {Label} from "@/components/ui/label"
import {cn} from "@/lib/utils"
import axiosInstance from "@/api/axios"
import {type ChartConfig, ChartContainer} from "@/components/ui/chart"
import {Button} from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

interface WasteData {
    period: string
    total: number
    organic: number
    paper: number
    pmd: number
    residual: number
    organic_freq: number
    paper_freq: number
    pmd_freq: number
    residual_freq: number
}

interface ExportData {
  title: string;
  headers: string[];
  rows: string[][];
  chartData: { x: string; y: number; }[];
}

// ── Color palettes ────────────────────────────────────────────────────────────
const DEFAULT_COLORS = {
    organic:  "hsl(var(--chart-6))",
    paper:    "hsl(var(--chart-8))",
    pmd:      "hsl(var(--chart-7))",
    residual: "hsl(var(--chart-9))",
}

const BELGIAN_COLORS = {
    organic:  "hsl(var(--chart-6))",  // unchanged
    paper:    "#E4981B",               // yellow
    pmd:      "#436DBC",               // blue
    residual: "hsl(var(--chart-9))",  // unchanged
}

const isBelgianBin = (binLocation: string) =>
    binLocation.startsWith("Seenons België")

const sampleData = [
    { period: "Jan", total: 7.35,    organic: 0,      paper: 4.25,  pmd: 2.8,   residual: 0.29, organic_freq: 0,  paper_freq: 73,  pmd_freq: 26, residual_freq: 4  },
    { period: "Feb", total: 1201.3,  organic: 474.88, paper: 160.06,pmd: 509.41,residual: 56.94,organic_freq: 22, paper_freq: 102, pmd_freq: 86, residual_freq: 19 },
    { period: "Mar", total: 1433,    organic: 288,    paper: 126,   pmd: 899,   residual: 120,  organic_freq: 9,  paper_freq: 42,  pmd_freq: 50, residual_freq: 8  },
    { period: "Apr", total: 225,     organic: 53,     paper: 6,     pmd: 136,   residual: 30,   organic_freq: 2,  paper_freq: 2,   pmd_freq: 8,  residual_freq: 2  },
]

interface WasteChartProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string
    title?: string
    description?: string
    timeRange?: string
    bins?: string[]
    useSampleData?: boolean
    customDateRange?: Array<{ startDate: Date | null; endDate: Date | null; key: string; }>;
    isCustomDateRange?: boolean;
}

export function WasteComposition({
    className,
    title,
    description,
    timeRange,
    bins = [],
    useSampleData = false,
    customDateRange = [{ startDate: null, endDate: null, key: 'selection' }],
    isCustomDateRange = false,
    ...props
}: WasteChartProps) {
    const { t } = useTranslation();
    const exportRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<WasteData[]>(useSampleData ? sampleData : [])
    const [isFrequency, setIsFrequency] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(!useSampleData)
    const [trend, setTrend] = useState<{ value: number; isUp: boolean }>({value: 0, isUp: true})
    const [isBelgian, setIsBelgian] = useState<boolean>(false)

    // ── Detect whether ALL selected bins are Belgian ──────────────────────────
    useEffect(() => {
        if (!bins || bins.length === 0) {
            setIsBelgian(false)
            return
        }
        axiosInstance.get('/get_bins')
            .then(res => {
                const allBins: { unique_identifier: string; bin_location: string }[] = res.data
                const selectedBinData = allBins.filter(b => bins.includes(b.unique_identifier))
                const allBelgian =
                    selectedBinData.length > 0 &&
                    selectedBinData.every(b => isBelgianBin(b.bin_location))
                setIsBelgian(allBelgian)
            })
            .catch(() => setIsBelgian(false))
    }, [bins])

    const wasteColors = isBelgian ? BELGIAN_COLORS : DEFAULT_COLORS

    const chartConfig = React.useMemo(() => ({
        organic:      { label: t('wasteComposition.wasteTypes.organic'),  color: wasteColors.organic  },
        paper:        { label: t('wasteComposition.wasteTypes.paper'),    color: wasteColors.paper    },
        pmd:          { label: t('wasteComposition.wasteTypes.pmd'),      color: wasteColors.pmd      },
        residual:     { label: t('wasteComposition.wasteTypes.residual'), color: wasteColors.residual },
        organic_freq: { label: t('wasteComposition.wasteTypes.organic'),  color: wasteColors.organic  },
        paper_freq:   { label: t('wasteComposition.wasteTypes.paper'),    color: wasteColors.paper    },
        pmd_freq:     { label: t('wasteComposition.wasteTypes.pmd'),      color: wasteColors.pmd      },
        residual_freq:{ label: t('wasteComposition.wasteTypes.residual'), color: wasteColors.residual },
        label:        { label: t('wasteComposition.label'),               color: "hsl(var(--foreground))" },
    } satisfies ChartConfig), [t, isBelgian]);

    const convertToCSV = (data: WasteData[]) => {
        if (!data || data.length === 0) return "";
        const headers = [
            t('wasteComposition.export.period'), t('wasteComposition.export.total'),
            t('wasteComposition.wasteTypes.organic'), t('wasteComposition.wasteTypes.paper'),
            t('wasteComposition.wasteTypes.pmd'), t('wasteComposition.wasteTypes.residual'),
            t('wasteComposition.export.organicFreq'), t('wasteComposition.export.paperFreq'),
            t('wasteComposition.export.pmdFreq'), t('wasteComposition.export.residualFreq')
        ];
        const rows = data.map(item => [
            `"${item.period}"`, item.total.toFixed(3), item.organic.toFixed(3),
            item.paper.toFixed(3), item.pmd.toFixed(3), item.residual.toFixed(3),
            item.organic_freq, item.paper_freq, item.pmd_freq, item.residual_freq
        ].join(","));
        return [headers.join(","), ...rows].join("\n");
    };

    const handleExportCSV = () => {
        if (data.length === 0) return;
        const csvContent = convertToCSV(data);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `waste_composition_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        if (useSampleData) { calculateTrend(sampleData); return; }
        if (!isCustomDateRange && !timeRange) return;
        if (!bins || bins.length === 0) return;

        const fetchData = async () => {
            setIsLoading(true)
            try {
                const params: any = { bins: bins.join(",") };
                if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                    const startDate = customDateRange[0].startDate;
                    const endDate = customDateRange[0].endDate;
                    params.start_date = startDate instanceof Date ? startDate.toISOString() : typeof startDate === 'string' ? startDate : new Date(startDate).toISOString();
                    params.end_date   = endDate   instanceof Date ? endDate.toISOString()   : typeof endDate   === 'string' ? endDate   : new Date(endDate).toISOString();
                } else {
                    params.timeRange = timeRange;
                }
                const response = await axiosInstance.get("/sub_waste_composition", {
                    params,
                    paramsSerializer: { indexes: null },
                })
                if (response.data && Array.isArray(response.data)) {
                    setData(response.data)
                    calculateTrend(response.data)
                } else {
                    setData([])
                }
            } catch (error) {
                console.error("Error fetching waste composition data:", error)
                setData([])
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [timeRange, bins, useSampleData, customDateRange, isCustomDateRange])

    const calculateTrend = (data: WasteData[]) => {
        if (data.length < 2) { setTrend({value: 0, isUp: true}); return; }
        const lastPeriod = data[data.length - 1]
        const previousPeriod = data[data.length - 2]
        const lastTotal = isFrequency
            ? lastPeriod.organic_freq + lastPeriod.paper_freq + lastPeriod.pmd_freq + lastPeriod.residual_freq
            : lastPeriod.total
        const prevTotal = isFrequency
            ? previousPeriod.organic_freq + previousPeriod.paper_freq + previousPeriod.pmd_freq + previousPeriod.residual_freq
            : previousPeriod.total
        if (prevTotal === 0) { setTrend({value: 100, isUp: true}); return; }
        const trendValue = ((lastTotal - prevTotal) / prevTotal) * 100
        setTrend({ value: Math.abs(Number.parseFloat(trendValue.toFixed(3))), isUp: trendValue >= 0 })
    }

    useEffect(() => { if (data.length > 0) calculateTrend(data) }, [isFrequency])

    const getDataKeys = () => {
        const baseKeys = isFrequency
            ? ["organic_freq", "paper_freq", "pmd_freq", "residual_freq"]
            : ["organic", "paper", "pmd", "residual"]
        return baseKeys.filter(key => data.some(item => Number(item[key as keyof WasteData]) > 0));
    }

    const getTotalForPeriod = (period: WasteData) => {
        if (isFrequency) return period.organic_freq + period.paper_freq + period.pmd_freq + period.residual_freq
        return period.total
    }

    const getFilteredData = () => data.filter(period => {
        if (isFrequency) return period.organic_freq > 0 || period.paper_freq > 0 || period.pmd_freq > 0 || period.residual_freq > 0;
        return period.organic > 0 || period.paper > 0 || period.pmd > 0 || period.residual > 0;
    });

    const filteredData = getFilteredData();

    const getExportData = React.useCallback((): ExportData => {
        const headers = [
            t('wasteComposition.export.period'), t('wasteComposition.export.total'),
            t('wasteComposition.wasteTypes.organic'), t('wasteComposition.wasteTypes.paper'),
            t('wasteComposition.wasteTypes.pmd'), t('wasteComposition.wasteTypes.residual')
        ];
        if (isFrequency) headers.push(
            t('wasteComposition.export.organicFreq'), t('wasteComposition.export.paperFreq'),
            t('wasteComposition.export.pmdFreq'), t('wasteComposition.export.residualFreq')
        );
        const rows = data.map(item => [
            `"${item.period}"`, item.total.toFixed(3), item.organic.toFixed(3),
            item.paper.toFixed(3), item.pmd.toFixed(3), item.residual.toFixed(3),
            ...(isFrequency ? [item.organic_freq.toString(), item.paper_freq.toString(), item.pmd_freq.toString(), item.residual_freq.toString()] : [])
        ]);
        return {
            title: title || t('wasteComposition.defaultTitle'),
            headers,
            rows,
            chartData: data.map(item => ({
                x: item.period,
                y: isFrequency
                    ? item.organic_freq + item.paper_freq + item.pmd_freq + item.residual_freq
                    : item.total
            }))
        };
    }, [data, isFrequency, title, t]);

    const handleExportPNG = () => {
        if (!exportRef.current) return;
        const controls = exportRef.current.querySelector('.export-controls') as HTMLElement;
        if (controls) controls.style.display = 'none';
        import('html2canvas').then((html2canvas) => {
            html2canvas.default(exportRef.current!, { scale: 2, logging: false, useCORS: true })
                .then((canvas) => {
                    const link = document.createElement('a');
                    link.download = `waste_composition_chart_${timeRange || 'data'}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                }).finally(() => { if (controls) controls.style.display = ''; });
        }).catch(err => {
            console.error('Failed to export as PNG:', err);
            if (controls) controls.style.display = '';
        });
    };

    return (
        <div
            ref={exportRef}
            className={cn("lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section", className)}
            data-export-data={JSON.stringify(getExportData())}
            {...props}
        >
            <div className="relative">
                <div className="flex justify-between items-center pb-1">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{title || t('wasteComposition.defaultTitle')}</h4>
                        {description && <p className="text-sm text-muted-foreground">{description}</p>}
                        {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {new Date(customDateRange[0].startDate).toLocaleDateString()} - {new Date(customDateRange[0].endDate).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center space-x-1 export-controls">
                        <div className="flex items-center space-x-1 bg-muted/20 px-2 py-1 rounded-md">
                            <Label htmlFor="frequency-mode" className={cn("text-xs", !isFrequency && "text-primary")}>
                                {t('wasteComposition.units.kg')}
                            </Label>
                            <Switch
                                id="frequency-mode"
                                checked={isFrequency}
                                onCheckedChange={setIsFrequency}
                                disabled={data.length === 0 || isLoading}
                                className="h-4 w-7"
                            />
                            <Label htmlFor="frequency-mode" className={cn("text-xs", isFrequency && "text-primary")}>
                                {t('wasteComposition.units.freq')}
                            </Label>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" disabled={data.length === 0 || isLoading}>
                                    <Download className="h-4 w-4 mr-1" />
                                    {t('wasteComposition.export.export')}
                                    <ChevronDown className="h-4 w-4 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleExportCSV} disabled={data.length === 0 || isLoading}>
                                    {t('wasteComposition.export.downloadCSV')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportPNG} disabled={data.length === 0 || isLoading}>
                                    {t('wasteComposition.export.downloadPNG')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <CircleHelp className="h-5 w-5 text-muted-foreground"/>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p dangerouslySetInnerHTML={{
                                        __html: t('wasteComposition.help.description') + '<br/><br/><strong>Date Range:</strong> Use the calendar in the header to select a custom date range'
                                    }} />
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>
            <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{width: '100%', height: '100%'}}>
                {isLoading ? (
                    <div className="flex items-center justify-center h-full w-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : data.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer width="100%" height={100}>
                            <BarChart
                                data={filteredData}
                                layout="vertical"
                                margin={{ top: 1, right: 110, left: 10, bottom: 20 }}
                                stackOffset="expand"
                            >
                                <CartesianGrid horizontal strokeDasharray="3 3" vertical={false}/>
                                <YAxis
                                    dataKey="period"
                                    type="category"
                                    width={60}
                                    tick={{fontSize: 12}}
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={10}
                                />
                                <XAxis type="number" hide/>
                                <Tooltip
                                    content={({active, payload, label}) => {
                                        if (active && payload && payload.length) {
                                            const filteredPayload = payload.filter((entry: any) => Number(entry.value) > 0);
                                            if (filteredPayload.length === 0) return null;
                                            const periodData = filteredData.find((d) => d.period === label) as WasteData;
                                            const total = getTotalForPeriod(periodData);
                                            return (
                                                <div className="rounded-lg border bg-background p-3 shadow-md">
                                                    <div className="font-medium mb-2">{label}</div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {filteredPayload.map((entry: any, index: number) => (
                                                            <React.Fragment key={`item-${index}`}>
                                                                <div className="flex items-center">
                                                                    <div className="mr-1 h-2 w-2 rounded-full" style={{backgroundColor: entry.color}}/>
                                                                    <div>{entry.name}</div>
                                                                </div>
                                                                <div className="text-right font-medium whitespace-nowrap">
                                                                    {isFrequency ? entry.value : `${Number(entry.value).toFixed(3)}\u00A0${t('wasteComposition.units.kg')}`}
                                                                </div>
                                                            </React.Fragment>
                                                        ))}
                                                        {total > 0 && (
                                                            <>
                                                                <div className="font-medium">{t('wasteComposition.export.total')}</div>
                                                                <div className="text-right font-medium whitespace-nowrap">
                                                                    {isFrequency ? total : `${Number(total).toFixed(3)}\u00A0${t('wasteComposition.units.kg')}`}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        }
                                        return null
                                    }}
                                />
                                <Legend/>
                                {getDataKeys().map((key, index) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        stackId="a"
                                        fill={chartConfig[key as keyof typeof chartConfig].color}
                                        name={chartConfig[key as keyof typeof chartConfig].label}
                                        radius={index === getDataKeys().length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                                    >
                                        {index === getDataKeys().length - 1 && (
                                            <LabelList
                                                dataKey={(entry) => getTotalForPeriod(entry as unknown as WasteData)}
                                                position="right"
                                                offset={8}
                                                className="fill-foreground"
                                                fontSize={12}
                                                formatter={(value: number) => (isFrequency ? value : `${value.toFixed(3)}\u00A0${t('wasteComposition.units.kg')}`)}
                                            />
                                        )}
                                    </Bar>
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                ) : (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('wasteComposition.states.noDataAvailable')}</p>
                            <p className="text-sm">{t('wasteComposition.states.selectTimeRangeAndBins')}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}